/**
 * AI 编辑器 Tab favicon 五态合成入口。
 *
 * 用项目 logo 合成一组带右下 sparkles 角标的 favicon：
 *  - `default`：无状态点，Tab idle
 *  - `done` / `paused` / `error`：右上叠加绿 / 黄 / 橙状态点
 *  - `building`：右上红色呼吸圆点，返回 RenderFunction，供 500ms canvas loop 调用
 *
 * 前端（GogoEditor）与长任务（GogoEditorRemote）共用同一合成实现，
 * 前者写入 `window.GOGO_CONFIG.favicon`，后者作为 prop 交给 CDN。
 */
import { composeAnimatedFavicon, composeStaticFavicon } from '@/utils/renderProjectFavicon';
import { IS_03_HOST } from '@/modules/lingshan/const';
const LINGSHAN_PLATFORM_FAVICON = {
    default: 'https://wfiles.gtimg.cn/wuji_dashboard/wupload/xy/starter/95d2fa12.png',
    building: 'https://wfiles.gtimg.cn/wuji_dashboard/wupload/xy/starter/95d2fa12.png',
    done: 'https://wfiles.gtimg.cn/wuji_dashboard/wupload/xy/starter/854d9f01.png',
    paused: 'https://wfiles.gtimg.cn/wuji_dashboard/wupload/xy/starter/988cd676.png',
    error: 'https://wfiles.gtimg.cn/wuji_dashboard/wupload/xy/starter/988cd676.png',
};
/**
 * 返回当前部署环境的平台占位 favicon 五态，或 undefined（无极场景）。
 * 用于在应用标合成完成前作为初始占位，两条链共用：
 *  - GogoEditor：写入 GOGO_CONFIG.favicon
 *  - GogoEditorRemote：作为 :favicon prop 的初始值
 *
 * - 03（Lingshan）：三张成品图，building/default 共用同一张
 * - 私有化：brandInfo.favicon 五态共用同一张平台图
 * - 无极默认：返回 undefined，各链按自身兜底处理
 */
export function getPlatformFavicon() {
    if (IS_03_HOST) {
        return LINGSHAN_PLATFORM_FAVICON;
    }
    const brandFavicon = window.GLOBAL_INFO?.brandInfo?.favicon;
    if (brandFavicon) {
        return {
            default: brandFavicon,
            building: brandFavicon,
            done: brandFavicon,
            paused: brandFavicon,
            error: brandFavicon,
        };
    }
    return undefined;
}
/**
 * 合成 AI 编辑器五态 favicon。
 * 静态图任一失败会返回 null，调用方保持平台占位；building 是纯函数，不参与失败判定。
 */
export async function composeEditorFaviconSet(logoUrl) {
    if (!logoUrl)
        return null;
    const [defaultIcon, doneIcon, pausedIcon, errorIcon] = await Promise.all([
        composeStaticFavicon({ logoUrl, corner: 'editor' }),
        composeStaticFavicon({ logoUrl, corner: 'editor', status: 'done' }),
        composeStaticFavicon({ logoUrl, corner: 'editor', status: 'paused' }),
        composeStaticFavicon({ logoUrl, corner: 'editor', status: 'error' }),
    ]);
    if (!defaultIcon)
        return null;
    return {
        default: defaultIcon,
        done: doneIcon,
        paused: pausedIcon,
        error: errorIcon,
        building: composeAnimatedFavicon({ logoUrl, corner: 'editor' }),
    };
}
